import { ILegacyCartEntry, IPrices } from '@rbi-ctg/menu';
import { IRestaurant } from '@rbi-ctg/store';
import { ItemAvailabilityStatus } from 'enums/menu';
import { ServiceMode } from 'generated/graphql-gateway';
import { getPosVendorFromStore } from 'hooks/menu/get-pos-vendor-from-store';
import { IDayPartBoundary } from 'state/menu/main-menu/types';
import { ILocalizedMenuEntry, IndexedMenu, PosServiceMode } from 'state/unified-menu/types';
import { isEntryAvailable } from 'state/unified-menu/utils';
import { isDummyItem } from 'utils/cart/is-dummy-item';
import { CartEntryType } from 'utils/cart/types';
import { PosVendors } from 'utils/vendor-config';

import { itemIsAvailable, itemOptionModifierPluIsAvailable } from './menu-availability';
import { getOneMenuAvailability } from './one-menu-availability';

export const getUnavailableCartEntries = (
  cartEntries: ILegacyCartEntry[],
  restaurant: IRestaurant,
  prices: IPrices,
  serviceMode?: ServiceMode
): {
  unavailableCartEntries: ILegacyCartEntry[];
  unavailableChildrenCartEntries: ILegacyCartEntry[];
} => {
  const vendor = getPosVendorFromStore(restaurant, serviceMode);

  const unavailableCartEntries: ILegacyCartEntry[] = [];
  const unavailableChildrenCartEntries: ILegacyCartEntry[] = [];

  cartEntries.forEach(cartEntry => {
    const availability = getCartEntryAvailability(cartEntry, vendor, prices);

    unavailableCartEntries.push(...availability.unavailableEntries);
    unavailableChildrenCartEntries.push(...availability.unavailableChildrenEntries);
  });

  return {
    unavailableCartEntries,
    unavailableChildrenCartEntries,
  };
};

interface AvailabilityResult {
  unavailableEntries: ILegacyCartEntry[];
  unavailableChildrenEntries: ILegacyCartEntry[];
}

export const getCartEntryAvailability = (
  cartEntry: ILegacyCartEntry,
  vendor: PosVendors | null,
  prices: IPrices,
  associatedItem?: ILegacyCartEntry,
  isRootEntry = true
): AvailabilityResult => {
  const isAvailable = (entry: ILegacyCartEntry) => {
    switch (entry.type) {
      case CartEntryType.combo:
        // we don't use comboIsAvailable here
        return itemIsAvailable(entry, vendor, prices);
      case CartEntryType.item:
        return itemIsAvailable(entry, vendor, prices);
      case CartEntryType.itemOptionModifier:
        // we only really need to know if the plu is available
        if (!associatedItem || !vendor) {
          return false;
        }

        return itemOptionModifierPluIsAvailable({
          item: associatedItem,
          itemOptionModifier: entry,
          vendor,
          prices,
        });
      case CartEntryType.comboSlot:
      case CartEntryType.itemOption:
        return true;
      case CartEntryType.offerCombo:
        return itemIsAvailable(entry, vendor, prices);
      case CartEntryType.offerItem:
        return itemIsAvailable(entry, vendor, prices);
      default:
        return false; // this shouldn't happen
    }
  };

  // hacking in a way to remember what the associated IItem is at every step
  const i = cartEntry.type === CartEntryType.item ? cartEntry : associatedItem;

  const currentEntryAvailable = isAvailable(cartEntry);

  const childrenResults = (cartEntry.children || []).map(child =>
    getCartEntryAvailability(child, vendor, prices, i, false)
  );

  const unavailableChildrenEntries = childrenResults.flatMap(result => [
    ...result.unavailableEntries,
    ...result.unavailableChildrenEntries,
  ]);

  const currentEntryUnavailable = !currentEntryAvailable;
  const hasUnavailableChildren = unavailableChildrenEntries.length > 0;

  const entryIsUnavailable = currentEntryUnavailable || hasUnavailableChildren;

  return {
    unavailableEntries: isRootEntry && entryIsUnavailable ? [cartEntry] : [],
    unavailableChildrenEntries: [
      ...(!isRootEntry && currentEntryUnavailable ? [cartEntry] : []),
      ...unavailableChildrenEntries,
    ],
  };
};

export function isCartEntryAvailableOneMenu(
  cartEntry: ILegacyCartEntry,
  menu: IndexedMenu<ILocalizedMenuEntry>,
  posServiceMode: PosServiceMode,
  activeDayParts: IDayPartBoundary[],
  storeHasBurgersForBreakfast: boolean
): boolean {
  if (
    [
      CartEntryType.combo,
      CartEntryType.item,
      CartEntryType.offerCombo,
      CartEntryType.offerItem,
      CartEntryType.itemOptionModifier,
    ].includes(cartEntry.type) &&
    !isDummyItem(cartEntry)
  ) {
    const menuEntry = menu.find(cartEntry._id);
    if (!menuEntry) {
      return false;
    }

    const availabilityStatus = getOneMenuAvailability(
      menuEntry,
      activeDayParts,
      posServiceMode,
      Boolean(storeHasBurgersForBreakfast),
      isEntryAvailable
    );

    return availabilityStatus === ItemAvailabilityStatus.AVAILABLE;
  }

  return cartEntry.children.every(child =>
    isCartEntryAvailableOneMenu(
      child,
      menu,
      posServiceMode,
      activeDayParts,
      storeHasBurgersForBreakfast
    )
  );
}

interface OneMenuAvailabilityResult {
  unavailableEntries: ILegacyCartEntry[];
  unavailableChildrenEntries: ILegacyCartEntry[];
}

export const getCartEntryAvailabilityOneMenu = (
  cartEntry: ILegacyCartEntry,
  menu: IndexedMenu<ILocalizedMenuEntry>,
  posServiceMode: PosServiceMode,
  activeDayParts: IDayPartBoundary[],
  storeHasBurgersForBreakfast: boolean,
  isRootEntry = true
): OneMenuAvailabilityResult => {
  const currentEntryAvailable = isCartEntryAvailableOneMenu(
    {
      ...cartEntry,
      children: [],
    },
    menu,
    posServiceMode,
    activeDayParts,
    storeHasBurgersForBreakfast
  );

  const childrenResults = (cartEntry.children || []).map(child =>
    getCartEntryAvailabilityOneMenu(
      child,
      menu,
      posServiceMode,
      activeDayParts,
      storeHasBurgersForBreakfast,
      false
    )
  );

  const unavailableChildrenEntries = childrenResults.flatMap(result => [
    ...result.unavailableEntries,
    ...result.unavailableChildrenEntries,
  ]);

  const currentEntryUnavailable = !currentEntryAvailable;
  const hasUnavailableChildren = unavailableChildrenEntries.length > 0;

  const entryIsUnavailable = currentEntryUnavailable || hasUnavailableChildren;

  return {
    unavailableEntries: isRootEntry && entryIsUnavailable ? [cartEntry] : [],
    unavailableChildrenEntries: [
      ...(!isRootEntry && currentEntryUnavailable ? [cartEntry] : []),
      ...unavailableChildrenEntries,
    ],
  };
};
